import { useState } from 'react';
import type { CheckoutOperation, CreateCheckoutResponse, CreateLaunchResponse } from './types';
import { isStableDemoUrlShareReady } from '../../lib/adminConfig';
import { normalizeLegacyDemoUrl, normalizeOneTimeCheckoutUrl, normalizeStableDemoUrl, sanitizeDisplayMessage } from '../../lib/adminApi';

interface OneTimeLinksProps {
  result: CreateLaunchResponse | CreateCheckoutResponse;
  onBack: () => void;
  checkoutOnly?: boolean;
  clinicName?: string;
  clinicSlug?: string;
  contactEmail?: string;
  onViewLaunch?: () => void;
  recoveryMessage?: string;
  checkoutOperation?: CheckoutOperation;
}

export function OneTimeLinks({
  result,
  onBack,
  checkoutOnly,
  clinicName,
  clinicSlug,
  contactEmail,
  onViewLaunch,
  recoveryMessage,
  checkoutOperation = 'initial',
}: OneTimeLinksProps) {
  const [copyError, setCopyError] = useState<string | null>(null);
  const [showDismissConfirm, setShowDismissConfirm] = useState(false);
  const [stableUrlCopyError, setStableUrlCopyError] = useState<string | null>(null);
  const [accessCodeCopyError, setAccessCodeCopyError] = useState<string | null>(null);
  const [showCodeDismissConfirm, setShowCodeDismissConfirm] = useState(false);

  const copyToClipboard = async (value: string | undefined, setError: (msg: string | null) => void = setCopyError) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setError(null);
    } catch {
      // Never include the raw value in the fallback copy or log it — this can
      // carry a bearer credential (demo access code) and must not leak via
      // console/error text (R14/TS6).
      setError('Copy failed. Select the value manually and copy it.');
    }
  };

  if (checkoutOnly) {
    const checkoutResult = result as CreateCheckoutResponse;
    const checkoutUrl = normalizeOneTimeCheckoutUrl(checkoutResult.checkout_url);
    const hasCheckoutUrl = Boolean(checkoutUrl);

    const handleDismiss = () => {
      setShowDismissConfirm(false);
      onBack();
    };

    return (
      <div className={`one-time-panel${hasCheckoutUrl ? '' : ' one-time-panel--recovery'}`} data-testid="one-time-links-checkout">
        <h3>
          {hasCheckoutUrl
            ? checkoutOperation === 'regenerate'
              ? 'Fresh payment link ready'
              : 'Payment link ready'
            : 'Payment link unavailable'}
        </h3>
        <p className={hasCheckoutUrl ? 'one-time-warning' : 'copy-fallback'}>
          {hasCheckoutUrl
            ? 'This one-time payment link is shown once and is not stored. Copy it now.'
            : 'No payment URL was returned. Refresh the launch detail to check current payment-link state.'}
        </p>

        {(clinicName || clinicSlug) && (
          <div className="form-row">
            <label htmlFor="one-time-checkout-clinic-name">Clinic</label>
            <div className="link-field">
              <input id="one-time-checkout-clinic-name" readOnly value={clinicName || clinicSlug} data-testid="one-time-clinic-name" />
            </div>
          </div>
        )}

        {contactEmail && (
          <div className="form-row">
            <label htmlFor="one-time-contact-email">Contact</label>
            <div className="link-field">
              <input id="one-time-contact-email" readOnly value={contactEmail} data-testid="one-time-contact-email" />
            </div>
          </div>
        )}

        {checkoutResult.launch_id && (
          <div className="form-row">
            <label htmlFor="one-time-checkout-launch-id">Launch ID</label>
            <div className="link-field">
              <input id="one-time-checkout-launch-id" readOnly value={checkoutResult.launch_id} data-testid="one-time-launch-id" />
            </div>
          </div>
        )}

        {hasCheckoutUrl ? (
          <div className="form-row" data-testid="one-time-checkout-row">
            <label htmlFor="one-time-checkout-url">{checkoutOperation === 'regenerate' ? 'Fresh payment link' : 'Payment link'}</label>
            <div className="link-field">
              <input
                id="one-time-checkout-url"
                readOnly
                value={checkoutUrl}
                data-testid="one-time-checkout-url"
              />
              <button
                className="btn btn--secondary"
                onClick={() => copyToClipboard(checkoutUrl)}
                data-testid="copy-checkout-url"
              >
                Copy
              </button>
              <a
                className="btn btn--secondary"
                href={checkoutUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-checkout-url"
              >
                Open
              </a>
            </div>
          </div>
        ) : (
          <div className="copy-fallback" data-testid="one-time-checkout-missing">
            {recoveryMessage
              ? sanitizeDisplayMessage(recoveryMessage)
              : 'No payment URL was returned. The checkout may exist, but the one-time URL is unavailable. Refresh the launch detail to check current state.'}
          </div>
        )}

        {copyError && <div className="copy-fallback">{copyError}</div>}

        <div className="actions">
          {onViewLaunch && (
            <button
              className="btn btn--secondary"
              onClick={onViewLaunch}
              data-testid="view-launch-button"
            >
              View originating launch
            </button>
          )}
          {!showDismissConfirm ? (
            <button
              className="btn btn--danger"
              onClick={() => setShowDismissConfirm(true)}
              data-testid="dismiss-checkout-button"
            >
              {hasCheckoutUrl ? 'Dismiss and clear payment link' : 'Dismiss result'}
            </button>
          ) : (
            <div className="dismiss-confirm" data-testid="dismiss-checkout-confirm">
              <p>{hasCheckoutUrl ? 'The payment link cannot be recovered after dismissal. Are you sure?' : 'Dismiss this recovery result?'}</p>
              <button className="btn btn--danger" onClick={handleDismiss} data-testid="confirm-dismiss">
                Yes, dismiss
              </button>
              <button className="btn btn--secondary" onClick={() => setShowDismissConfirm(false)} data-testid="cancel-dismiss">
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const launchResult = result as CreateLaunchResponse;
  const stableDemoUrl = normalizeStableDemoUrl(launchResult.demo.stable_demo_url);
  const legacyDemoUrl = normalizeLegacyDemoUrl(launchResult.demo.demo_url);
  const checkoutUrl = normalizeOneTimeCheckoutUrl(launchResult.checkout?.checkout_url);
  const hasStableUrl = Boolean(stableDemoUrl);
  const hasAccessCode = Boolean(launchResult.demo.demo_access_code);
  const hasLegacyDemoUrl = Boolean(legacyDemoUrl);
  const hasCheckoutUrl = Boolean(checkoutUrl);
  const showCheckoutRow = launchResult.checkout !== undefined;
  const shareReady = isStableDemoUrlShareReady();

  const handleDismissDemo = () => {
    setShowCodeDismissConfirm(false);
    onBack();
  };

  const checkoutRow = showCheckoutRow && (
    <div className="form-row" data-testid="one-time-checkout-row">
      <label htmlFor="one-time-created-checkout-url">Payment link</label>
      {hasCheckoutUrl ? (
        <div className="link-field">
          <input id="one-time-created-checkout-url" readOnly value={checkoutUrl} data-testid="one-time-checkout-url" />
          <button className="btn btn--secondary" onClick={() => copyToClipboard(checkoutUrl)} data-testid="copy-checkout-url">
            Copy
          </button>
          <a
            className="btn btn--secondary"
            href={checkoutUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="open-checkout-url"
          >
            Open
          </a>
        </div>
      ) : (
        <div className="copy-fallback" data-testid="one-time-checkout-missing">
          No payment URL was returned. This can happen when the response was replayed from an idempotency cache, or the checkout object was omitted. Copy the launch ID above and ask an engineer to generate a fresh checkout link through the approved internal recovery flow.
        </div>
      )}
    </div>
  );

  // New contract: backend returned a durable stable clinic URL. Show it and
  // the raw access code as independent copy targets (R5, TS1). The legacy
  // fragment-token demo_url path below is only used when stable_demo_url is
  // absent.
  if (hasStableUrl) {
    return (
      <div className="one-time-panel" data-testid="one-time-links">
        <h3>Demo clinic created</h3>
        <p className="one-time-warning">
          Copy the one-time values before leaving this workspace.
        </p>

        <div className="form-row">
          <label htmlFor="one-time-created-launch-id">Launch ID</label>
          <div className="link-field">
            <input id="one-time-created-launch-id" readOnly value={launchResult.launch_id} data-testid="one-time-launch-id" />
          </div>
        </div>

        <div className="form-row" data-testid="one-time-stable-url-row">
          <label htmlFor="one-time-stable-url">Stable clinic URL</label>
          <div className="link-field">
            <input id="one-time-stable-url" readOnly value={stableDemoUrl} data-testid="one-time-stable-url" />
            <button
              className="btn btn--secondary"
              onClick={() => copyToClipboard(stableDemoUrl, setStableUrlCopyError)}
              data-testid="copy-stable-url"
            >
              Copy
            </button>
            {shareReady && (
              <a
                className="btn btn--secondary"
                href={stableDemoUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-stable-url"
              >
                Open
              </a>
            )}
          </div>
          {shareReady ? (
            <p className="copy-fallback">
              This URL is durable and safe to share with the clinic repeatedly.
            </p>
          ) : (
            <p className="copy-fallback">
              Stable clinic URL sharing is not ready to share yet. Record this URL for internal use only.
            </p>
          )}
          {stableUrlCopyError && <div className="copy-fallback">{stableUrlCopyError}</div>}
        </div>

        <div className="form-row" data-testid="one-time-access-code-row">
          <label htmlFor="one-time-access-code">Demo access code</label>
          {hasAccessCode ? (
            <>
              <div className="link-field">
                <input id="one-time-access-code" readOnly value={launchResult.demo.demo_access_code} data-testid="one-time-access-code" />
                <button
                  className="btn btn--secondary"
                  onClick={() => copyToClipboard(launchResult.demo.demo_access_code, setAccessCodeCopyError)}
                  data-testid="copy-access-code"
                >
                  Copy
                </button>
              </div>
              <p className="one-time-warning">
                This access code cannot be recovered after you leave this screen. If it is lost, an intentional rotate is required to generate a new one.
              </p>
              {accessCodeCopyError && <div className="copy-fallback">{accessCodeCopyError}</div>}
            </>
          ) : (
            <div className="copy-fallback" data-testid="one-time-access-code-missing">
              No demo access code was returned with this stable URL. This can happen on an idempotency replay. Do not treat any existing code as still valid. Rotate access code once that action is available to generate a new one.
            </div>
          )}
        </div>

        {checkoutRow}

        {copyError && <div className="copy-fallback">{copyError}</div>}

        <div className="actions">
          {(hasAccessCode || hasCheckoutUrl) ? (
            !showCodeDismissConfirm ? (
              <button
                className="btn btn--danger"
                onClick={() => setShowCodeDismissConfirm(true)}
                data-testid="dismiss-demo-button"
              >
                Dismiss and clear values
              </button>
            ) : (
              <div className="dismiss-confirm" data-testid="dismiss-demo-confirm">
                <p>
                  {hasAccessCode && hasCheckoutUrl
                    ? 'The demo access code and payment link cannot be recovered after dismissal. Are you sure?'
                    : hasAccessCode
                      ? 'The demo access code cannot be recovered after dismissal. Are you sure?'
                      : 'The payment link cannot be recovered after dismissal. Are you sure?'}
                </p>
                <button className="btn btn--danger" onClick={handleDismissDemo} data-testid="confirm-dismiss-demo">
                  Yes, dismiss
                </button>
                <button className="btn btn--secondary" onClick={() => setShowCodeDismissConfirm(false)} data-testid="cancel-dismiss-demo">
                  Cancel
                </button>
              </div>
            )
          ) : (
            <button className="btn" onClick={onBack} data-testid="one-time-back">
              Back to create form
            </button>
          )}
        </div>
      </div>
    );
  }

  const handleDismissLegacy = () => {
    setShowCodeDismissConfirm(false);
    onBack();
  };

  return (
    <div className="one-time-panel" data-testid="one-time-links">
      <h3>Demo clinic created</h3>
      <p className="one-time-warning">
        These links are shown once and are not stored. Copy them now.
      </p>

      <div className="form-row">
        <label htmlFor="one-time-legacy-launch-id">Launch ID</label>
        <div className="link-field">
          <input id="one-time-legacy-launch-id" readOnly value={launchResult.launch_id} data-testid="one-time-launch-id" />
        </div>
      </div>

      <div className="form-row" data-testid="one-time-demo-row">
        <label htmlFor="one-time-demo-url">Demo link</label>
        {hasLegacyDemoUrl ? (
          <div className="link-field">
            <input id="one-time-demo-url" readOnly value={legacyDemoUrl} data-testid="one-time-demo-url" />
            <button className="btn btn--secondary" onClick={() => copyToClipboard(legacyDemoUrl)} data-testid="copy-demo-url">
              Copy
            </button>
          </div>
        ) : (
          <div className="copy-fallback" data-testid="one-time-demo-missing">
            No demo URL was returned. This can happen when the response was replayed from an idempotency cache. Copy the launch ID above and ask an engineer to generate a fresh demo link through the approved internal recovery flow.
          </div>
        )}
      </div>

      {checkoutRow}

      {copyError && <div className="copy-fallback">{copyError}</div>}

      <div className="actions">
        {(hasLegacyDemoUrl || hasCheckoutUrl) ? (
          !showCodeDismissConfirm ? (
            <button
              className="btn btn--danger"
              onClick={() => setShowCodeDismissConfirm(true)}
              data-testid="dismiss-demo-button"
            >
              Dismiss and clear values
            </button>
          ) : (
            <div className="dismiss-confirm" data-testid="dismiss-demo-confirm">
              <p>
                {hasLegacyDemoUrl && hasCheckoutUrl
                  ? 'The demo link and payment link cannot be recovered after dismissal. Are you sure?'
                  : hasLegacyDemoUrl
                    ? 'The demo link cannot be recovered after dismissal. Are you sure?'
                    : 'The payment link cannot be recovered after dismissal. Are you sure?'}
              </p>
              <button className="btn btn--danger" onClick={handleDismissLegacy} data-testid="confirm-dismiss-demo">
                Yes, dismiss
              </button>
              <button className="btn btn--secondary" onClick={() => setShowCodeDismissConfirm(false)} data-testid="cancel-dismiss-demo">
                Cancel
              </button>
            </div>
          )
        ) : (
          <button className="btn" onClick={onBack} data-testid="one-time-back">
            Back to demo clinic list
          </button>
        )}
      </div>
    </div>
  );
}
